// @ts-nocheck
import { Component } from 'react'
import { css, StyleSheet } from 'aphrodite/no-important'

import Modal from '.'
import PopupModal from './PopupModal'
import { FormFieldLayout, Icon } from '../generic'
import { postJSON } from '../../common/services/api'
import { colors } from '@lmc/common/constants'
import { ImportantContact } from '@lmc/ts-common'
import { darken, lighten } from '../../LEGACY/colorUtils'
import { isArray } from 'lodash'
import { theme } from '@lmc/ui-kit'

interface Props {
  importantContact: ImportantContact
  residentId: string
  index: number
  showToast: (message: { title: string; type: string }) => void
  fetchList: (list: string) => void
  close: () => void
  isOpen: boolean
  residentName: { first: string; last: string }
  features: { familyPaymentType: string }
  error?: boolean
}

export class InviteFamilyModal extends Component<
  Props,
  { comment: string; isChecked: boolean }
> {
  constructor(props: Props) {
    super(props)
    this.state = {
      comment: '',
      isChecked: false
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { importantContact } = this.props
    if (!prevProps.importantContact && importantContact) {
      this.setState({
        comment: importantContact.familyComment
          ? importantContact.familyComment
          : '',
        isChecked: false
      })
    }
  }

  handleSubmit = async () => {
    const { residentId, index, showToast } = this.props
    const url = `list/residents/${residentId}/invite-family/${index}`
    const { comment } = this.state
    const json = { familyComment: comment }

    try {
      const resident = await postJSON({ url, json })
      this.handleComplete(resident)
    } catch (error) {
      if (
        error.message.startsWith('E-mail already in use') ||
        error.userExists ||
        typeof error === 'string'
      ) {
        showToast({
          title: error.message,
          type: 'error'
        })
      } else {
        showToast({
          title: 'Something went wrong.',
          type: 'error'
        })
      }
    }
  }

  handleComplete = () => {
    const { fetchList, close, showToast } = this.props
    fetchList('residents')
    close()
    showToast({ title: 'Invited a Family User' })
  }

  renderCheckbox = () => {
    const { isChecked } = this.state
    const { error, importantContact } = this.props

    let borderColor
    if (error) {
      borderColor = colors.red
    } else if (isChecked) {
      borderColor = theme.color.status.primary
    } else {
      borderColor = colors.grey
    }

    return (
      <div style={styles.checkboxContainer}>
        <div
          className="lmc-option-field"
          onClick={() => this.setState({ isChecked: !isChecked })}
          style={{ borderColor }}
        >
          <div style={styles.valueContainer}>
            <span style={styles.checkboxLabel}>
              {`I have received permission from ${importantContact.name.first} to send invite`}
            </span>
          </div>
          <Icon
            name={isChecked ? 'checkbox-on' : 'checkbox-off'}
            fill={isChecked ? theme.color.status.primary : null}
            size={24}
            style={styles.icon}
          />
        </div>
      </div>
    )
  }

  render() {
    const {
      features: { familyPaymentType },
      isOpen,
      importantContact,
      residentName,
      ...formProps
    } = this.props
    if (!isOpen) return null

    return (
      <PopupModal
        confirmText="Send Invite"
        title="Invite to Family App"
        buttonColour="success"
        buttonDisabled={!this.state.isChecked}
        buttonIcon="MailOutlined"
        onConfirm={this.handleSubmit}
        supportArticle="https://support.logmycare.co.uk/"
        footerStyle={styles.footerStyle}
        {...formProps}
      >
        <div key="subheading" style={styles.subheading}>
          {`An email will be sent to ${importantContact.name.first} 
                    ${importantContact.name.last} with instructions on how to get 
                    set up with access to ${residentName.first} ${residentName.last}'s information.`}
        </div>
        {familyPaymentType === 'family' && (
          <div style={styles.subheading}>{PRICING_NOTE}</div>
        )}
        <div
          style={{ ...styles.infoContainer, justifyContent: 'space-between' }}
        >
          <div style={{ display: 'flex' }}>
            <div style={styles.labelContainer}> Name </div>
            <div>
              {' '}
              {`${importantContact.name.first} ${importantContact.name.last}`}{' '}
            </div>
          </div>
          {importantContact.lpa && isArray(importantContact.lpaTypes) && (
            <div>
              {importantContact.lpaTypes.map((type) => (
                <div style={{ ...styles.badge, ...styles.badgeLPA }}>
                  LPA {type}
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          style={{ ...styles.infoContainer, justifyContent: 'space-between' }}
        >
          <div style={{ display: 'flex' }}>
            <div style={styles.labelContainer}> Relationship </div>
            {importantContact.relationship ? (
              <div> {`${importantContact.relationship}`} </div>
            ) : (
              <div style={styles.noInfo}> No information entered </div>
            )}
          </div>
          {importantContact.relationship &&
            (importantContact.type === 'Professional' ? (
              <div style={{ ...styles.badge, ...styles.badgeProfessional }}>
                Professional
              </div>
            ) : (
              <div style={{ ...styles.badge, ...styles.badgePersonal }}>
                Personal
              </div>
            ))}
        </div>
        <div style={styles.infoContainer}>
          <div style={styles.labelContainer}> Email </div>
          <div> {`${importantContact.email}`} </div>
        </div>
        <div style={styles.fieldContainer}>
          <FormFieldLayout
            label="Why are you inviting them?"
            layout="column"
            labelNote="- Optional"
            labelStyle={{ fontWeight: 'bold' }}
          >
            <textarea
              className={css(classes.textarea)}
              onChange={(e) =>
                this.setState({
                  comment: e.target.value
                })
              }
              rows={2}
              value={this.state.comment}
            />
          </FormFieldLayout>
        </div>
        {this.renderCheckbox()}
      </PopupModal>
    )
  }
}

const MODAL_ID = 'inviteFamily'
const PRICING_NOTE =
  'After their one month free trial, they will receive a monthly charge of £9.99 (including VAT).'

const classes = StyleSheet.create({
  textarea: {
    borderRadius: 5,
    width: '100%',
    padding: '5px 10px 6px 10px',
    resize: 'none',
    borderColor: colors.grey,
    ':hover': {
      borderColor: theme.color.status.primary
    },
    ':focus': {
      borderColor: theme.color.status.primary,
      outline: 0
    }
  }
})

const styles = {
  badge: {
    alignSelf: 'flex-start',
    display: 'inline-block',
    fontWeight: 'bold',
    float: 'right',
    fontSize: 12,
    padding: '3px 12px',
    borderRadius: 15
  },
  badgeLPA: {
    background: colors.grey,
    color: colors.white,
    marginLeft: 10
  },
  badgeProfessional: {
    background: lighten(colors.darkblue, 80),
    color: darken(colors.darkblue, 30)
  },
  badgePersonal: {
    background: lighten(colors.purple, 80),
    color: darken(colors.purple, 30)
  },
  checkboxContainer: {
    display: 'flex'
  },
  checkboxLabel: {
    alignSelf: 'center',
    display: 'block',
    marginRight: 5,
    fontWeight: 600
  },
  fieldContainer: {
    paddingTop: 5,
    paddingBottom: 15
  },
  footerStyle: {
    justifyContent: 'space-between'
  },
  icon: {
    flexShrink: 0
  },
  infoContainer: {
    display: 'flex',
    flex: 'row',
    alignItems: 'center',
    paddingBottom: 20
  },
  labelContainer: {
    minWidth: 120,
    fontWeight: 'bold'
  },
  noInfo: {
    fontSize: 12,
    fontStyle: 'italic',
    color: colors.black,
    alignSelf: 'flex-end'
  },
  subheading: {
    fontSize: 12,
    paddingBottom: 20
  },
  valueContainer: {
    alignItems: 'center',
    display: 'flex'
  }
}

export default Modal(InviteFamilyModal, MODAL_ID)
